import { AppListActionButton } from "@/components/common/AppListToolbar";
import { requireTab } from "@/features/navigation/requireTab";
import { AppPageHeader } from "@/components/common/AppPageHeader";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { allValidationsQueryOptions } from "@/features/validations/api/selectAllValidations";
import { myPendingValidationsQueryOptions } from "@/features/validations/api/selectMyPendingValidations";
import { myValidationHistoryQueryOptions } from "@/features/validations/api/selectMyValidationHistory";
import AdminNewValidationDialog from "@/features/validations/components/AdminNewValidationDialog";
import { ValidationsInbox } from "@/features/validations/components/ValidationsInbox";
import { useAuth } from "@/utils/auth";
import { useNav } from "@/utils/nav";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

function ValidatePage() {
    const auth = useAuth();
    const isAdmin = auth.roles.isAdmin;
    const isValidator = auth.roles.isValidator;
    const [newValidationOpen, setNewValidationOpen] = useState(false);

    useNav([{ route: { to: "/validate" }, label: "Validate" }]);

    const description = isAdmin
        ? isValidator
            ? "Review items assigned to you. You can also complete a pending invite on a colleague's behalf, or record a sign-off received outside the app."
            : "Invite a colleague, complete a pending review on their behalf, or record a sign-off received outside the app."
        : "Confirm each item matches its source and sign off below.";

    const newValidationButton = (layout: "desktop" | "mobile") => (
        <AppListActionButton
            label="New validation"
            data-testid={layout === "mobile" ? "new-validation-button-mobile" : "new-validation-button"}
            layout={layout}
            onClick={() => setNewValidationOpen(true)}
        />
    );

    return (
        <AppPageLayout width="full" className="space-y-6">
            <AppPageHeader
                title="Validate"
                description={description}
                actions={isAdmin ? newValidationButton("desktop") : null}
            />
            <ValidationsInbox
                userId={auth.id}
                isAdmin={isAdmin}
                mobileAction={isAdmin ? newValidationButton("mobile") : undefined}
            />
            {isAdmin ? <AdminNewValidationDialog open={newValidationOpen} onOpenChange={setNewValidationOpen} /> : null}
        </AppPageLayout>
    );
}

export const Route = createFileRoute("/_app/validate/")({
    component: ValidatePage,
    beforeLoad: async ({ context }) => {
        const userId = context?.auth?.id;
        if (!userId || !context?.queryClient) {
            throw redirect({ to: "/login" });
        }

        // Roles are loaded in `/_app`'s loader, which runs after child beforeLoads -
        // resolve them here so a hard navigation to /validate doesn't treat a
        // validator (or admin) as a guest. Same pattern as /_app/builder.
        const roles = await context.queryClient.ensureQueryData(usersOrganisationsRolesQueryOptions(userId));
        context.auth.roles.setRoles(roles ?? []);

        await requireTab(context, "validate");

        return context;
    },
    loader: async ({ context }) => {
        const userId = context.auth.id;
        if (context.auth.roles.isAdmin) {
            await context.queryClient.ensureQueryData(allValidationsQueryOptions(0, "all", 1000));
            return;
        }
        await Promise.all([
            context.queryClient.ensureQueryData(myPendingValidationsQueryOptions(userId, 0, 1000)),
            context.queryClient.ensureQueryData(myValidationHistoryQueryOptions(userId, 0, undefined, 1000)),
        ]);
    },
});
